import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Shirt,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Sparkles,
  Users,
  Target,
  Camera,
  Brain,
  Eye,
  ShoppingBag,
  Zap,
  Shield,
  BarChart3,
  Palette,
  Scan,
  Play,
  ChevronRight,
  User,
  Wand2,
  RefreshCw,
  Box
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { styleSyncTestimonials } from '../../shared/testimonialData';

const StyleSync: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Palette,
      title: 'Personalised Outfit Recommendations',
      description: 'AI models learn from body profiles, style preferences, and behaviour to assemble outfits that match each shopper’s taste, dress code, and budget.'
    },
    {
      icon: Eye,
      title: 'Virtual Try-On Experience',
      description: 'Body scans, size data, and advanced rendering technology let users visualise how garments and accessories might look before they buy.'
    },
    {
      icon: BarChart3,
      title: '	Wardrobe Analytics & Smart Closet',
      description: 'For brands and corporate clients, StyleSync AI can power wardrobe dashboards that show utilisation, gaps, and recommendations for the next best item to purchase.'
    },
    {
      icon: Brain,
      title: 'Preference ML & Style Profiles',
      description: 'Preference models continuously learn from clicks, returns, likes, and purchase history to refine suggestions at both individual and cohort levels.'
    },
    {
      icon: ShoppingBag,
      title: 'Inventory Matching & Smart Merchandising',
      description: 'The engine maps user needs to current stock—boosting visibility for under-exposed items and improving sell-through without aggressive discounting.'
    },
    {
      icon: RefreshCw,
      title: 'Seamless E-Commerce Integrations',
      description: 'APIs plug directly into platforms like Myntra, Amazon, Flipkart, or your own storefront, keeping checkout and order management with your existing stack.'
    },
    {
      icon: Shield,
      title: 'Stylist Review & Collaboration Tools',
      description: 'Human stylists can review AI-created outfits, tweak looks, and add a human touch for premium or corporate customers.'
    },
    {
      icon: Zap,
      title: 'Multi-Channel Front-End Hooks',
      description: 'Power styling across your website, mobile app, in-store kiosks, and even corporate employee portals with a single intelligence layer.'
    }
  ];

  const stylingFlow = [
    {
      icon: User,
      title: 'User Profile',
      subtitle: 'Personal style preferences and body measurements',
      description: 'Capture user style preferences, body measurements, and fashion goals through interactive onboarding.',
      color: 'from-cyan-400 to-blue-500',
      step: 1
    },
    {
      icon: Camera,
      title: 'Image Analysis',
      subtitle: 'Computer vision processes user photos and preferences',
      description: 'Advanced computer vision analyzes user photos, existing wardrobe, and style preferences to understand individual fashion DNA.',
      color: 'from-blue-400 to-indigo-500',
      step: 2
    },
    {
      icon: Wand2,
      title: 'AI Recommendations',
      subtitle: 'Machine learning generates personalized style suggestions',
      description: 'ML algorithms process thousands of fashion data points to generate personalized outfit recommendations matching user preferences.',
      color: 'from-indigo-400 to-purple-500',
      step: 3
    },
    {
      icon: Scan,
      title: 'Virtual Try-On',
      subtitle: 'Augmented reality preview of recommended outfits',
      description: 'AR technology renders realistic previews of recommended outfits on user\'s body, enabling confident purchase decisions.',
      color: 'from-purple-400 to-pink-500',
      step: 4
    },
    {
      icon: ShoppingBag,
      title: 'Purchase & Sharing',
      subtitle: 'Seamless checkout and social sharing options',
      description: 'One-click checkout through integrated e-commerce APIs with social sharing features to get feedback from friends.',
      color: 'from-pink-400 to-rose-500',
      step: 5
    }
  ];

  const solutions = [
    {
      icon: Brain,
      title: 'Fashion E-Commerce Marketplaces',
      description: 'Increase conversion and average order value through AI-powered “complete the look” suggestions and virtual try-on on product detail pages.',
      features: ['Preference Learning', 'Trend Analysis', 'Body Type Matching', 'Occasion-Based Styling', 'Color Coordination']
    },
    {
      icon: Eye,
      title: 'Direct-to-Consumer Apparel Brands',
      description: 'Offer personalised style journeys that reduce decision fatigue and express your brand DNA consistently across channels',
      features: ['AR Rendering', '3D Body Mapping', 'Real-Time Preview', 'Size Recommendations', 'Fit Accuracy']
    },
    {
      icon: Box,
      title: 'Corporate Wardrobe & Uniform Programmes',
      description: 'Help organisations roll out standardised uniforms or dress codes while allowing employees to choose fits and combinations that suit them.',
      features: ['Digital Wardrobe', 'Usage Tracking', 'Mix & Match', 'Gap Analysis', 'Seasonal Planning']
    },
    {
      icon: ShoppingBag,
      title: 'Styling Subscriptions & Personal Shopper Services',
      description: 'Combine AI curation with stylist oversight to scale personalised boxes or digital styling sessions without sacrificing quality.',
      features: ['Multi-Retailer APIs', 'Price Comparison', 'Availability Tracking', 'One-Click Checkout', 'Order Management']
    },
    {
      icon: Zap,
      title: 'In-Store Digital Experiences',
      description: 'Use kiosks or mirrored displays to let shoppers try different sizes, colours, and combinations virtually while standing in the store.',
      features: ['Cross-Platform Sync', 'Unified Profiles', 'Consistent Recommendations', 'Channel Analytics', 'Seamless Transitions']
    }
  ];

  const compliances = [
    {
      icon: Users,
      title: 'Privacy-First Body Scans & Profiles',
      description: 'Body scans and measurements are stored securely, with clear consent flows and options for users to delete their profiles on request.',
      benefits: ['Higher Conversion Rates', 'Reduced Return Rates', 'Increased AOV', 'Better Engagement', 'Customer Insights']
    },
    {
      icon: Shirt,
      title: '	Encrypted Data Handling',
      description: 'All personally identifiable information, transaction data, and style profiles are encrypted in transit and at rest.All personally identifiable information, transaction data, and style profiles are encrypted in transit and at rest.',
      benefits: ['Brand Loyalty', 'Product Discovery', 'Collection Showcasing', 'Customer Data', 'Personalized Marketing']
    },
    {
      icon: Target,
      title: 'Scoped API Access & RBAC',
      description: 'Integration keys and fine-grained roles ensure that only appropriate systems and teams can access analytics, styling data, or user-level insights',
      benefits: ['Professional Styling', 'Budget Management', 'Dress Code Compliance', 'Employee Satisfaction', 'Bulk Ordering']
    },
    {
      icon: Sparkles,
      title: 'Compliance with Regional Regulations',
      description: 'Architecture and policies are aligned with major data protection frameworks, with options to localise data residency to specific regions where needed.',
      benefits: ['Affordable Styling', 'Accessible Expertise', 'Confidence Boost', 'Time Savings', 'Style Evolution']
    },
    {
      icon: BarChart3,
      title: 'Transparent Data Usage',
      description: 'Clear documentation and UI messaging explain how user data powers styling recommendations, helping build trust with both end customers and enterprise clients.',
      benefits: ['Continuous Improvement', 'Latest Trends', 'Adaptive Learning', 'User Feedback', 'Evolving Styles']
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Higher Conversion & Basket Size',
      description: 'More relevant outfit suggestions and frictionless try-on experiences turn hesitant browsers into confident buyers.',
      
    },
    {
      icon: TrendingUp,
      title: '	Reduced Returns & Size-Related Friction',
      description: 'Better fit predictions and visualisation help cut down on “trial and error” purchases that drive up logistics costs.',
    },
    {
      icon: Target,
      title: 'Merchandising Intelligence',
      description: 'Wardrobe analytics help buying and planning teams understand what style combinations work in the real world, not just in lookbooks.',
    },
    {
      icon: Shield,
      title: 'Stronger Brand Loyalty',
      description: 'Customers feel seen and understood when suggestions reflect their style and constraints, encouraging repeat visits and word-of-mouth.',
    },
    {
      icon: Users,
      title: 'Scalable Personalisation',
      description: 'StyleSync AI lets brands deliver “personal stylist” experiences to thousands or millions of shoppers without linear headcount growth.',
    },
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-purple-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 neon-glow">
                  <Shirt className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-purple-400 uppercase tracking-wide">AI Styling Engine</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent">StyleSync AI</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Fashion Styling Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
               StyleSync AI is a styling intelligence layer that sits behind your fashion storefront and wardrobe experiences. It studies user profiles, preferences, and inventory in real time to recommend outfits that feel curated—not random.With built-in virtual try-on and wardrobe analytics, StyleSync AI helps fashion e-commerce, brands, and corporate clients drive higher conversion, fewer returns, and stickier customer relationships. Shoppers can see how outfits look on their body type, discover new styles they’d actually wear, and check what works with pieces they already own.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500 to-pink-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-purple-500/25 transition-all duration-300"
                >
                  Request Platform Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-500">AI</div>
                  <div className="text-sm text-muted-foreground">Powered</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-pink-400">Virtual</div>
                  <div className="text-sm text-muted-foreground">Try-On</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">Personal</div>
                  <div className="text-sm text-muted-foreground">Styling</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/styleSync.png"
                    alt="StyleSync AI Fashion Styling Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Comprehensive AI-powered fashion styling capabilities
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* StyleSync AI Styling Flow - Hardcoded from Image */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              StyleSync AI <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Styling Flow</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-4 text-center">
              Styling Sequence
            </p>
            <p className="text-base text-muted-foreground max-w-4xl mx-auto text-center">
              The styling flow includes user profile & body scan, preference ML, inventory matching, virtual try-on render, stylist review, and purchase. APIs plug into Myntra, Amazon, Flipkart for seamless checkout.
            </p>
          </motion.div>

          {/* Hardcoded Flowchart from Provided Image */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 relative">
              {stylingFlow.map((step, index) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="glass-strong rounded-xl p-6 relative group hover:border-purple-500/50 transition-all duration-300"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="font-semibold text-lg mb-2 text-white">{step.title}</h3>
                        <p className="text-xs text-muted-foreground mb-3">{step.subtitle}</p>
                        <p className="text-xs text-muted-foreground/80 text-justify">{step.description}</p>
                      </div>
                      {/* Arrow */}
                      {index < stylingFlow.length - 1 && (
                        <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                          <ChevronRight className="w-6 h-6 text-purple-400" />
                        </div>
                      )}
                    </motion.div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Continuous Improvement Note */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 glass rounded-xl p-6 border border-purple-500/30"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center flex-shrink-0">
                  <RefreshCw className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Continuous Improvement</h3>
                  <p className="text-sm text-muted-foreground text-justify">
                    StyleSync AI continuously improves its styling capabilities, ensuring that users have access to the latest insights and tools for their fashion needs. The platform learns from user feedback, purchase behavior, and emerging fashion trends to refine recommendations over time.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{solution.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 hidden">
                    {solution.features.map((solution, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                        <span>{solution}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent"> Compliance</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-pink-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{compliance.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{compliance.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 hidden">
                    {compliance.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-pink-400 flex-shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Transform the fashion experience with intelligent automation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-purple-500/50 hover:border-purple-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-purple-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>

                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <ProductTestimonials 
          testimonials={styleSyncTestimonials}
          title="Fashion Excellence"
          subtitle="See how fashion brands transformed their styling with AI-powered platform"
          gradientFrom="#FF3D9A"
          gradientVia="#8A3FFC"
          gradientTo="#FF3D9A"
          iconColor="#FF3D9A"
          dotColor="bg-pink-500"
          speed={65}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-purple-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Revolutionize Your <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Fashion Business?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-center">
                Schedule a demo to see how StyleSync AI can transform your fashion styling, increase engagement, and deliver personalized experiences to your customers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-purple-500/30 hover:border-purple-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default StyleSync;